import React, { useState, useEffect, useCallback } from 'react';
import { ProcurementTableProps, ProcurementRequest } from '../types';
import StatusBadge from './StatusBadge';
import RequestDetailModal from './RequestDetailModal';
import CompletedRequestModal from './CompletedRequestModal';
import RequestEmailsModal from './RequestEmailsModal';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import { formatCurrency } from '../utils/currencyUtils';
import '../styles/ProcurementTable.css';

const ProcurementTable: React.FC<ProcurementTableProps> = ({ data, onRowClick, onRefreshClick, onEditClick }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<string>('user');
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [selectedRequestForPDF, setSelectedRequestForPDF] = useState<ProcurementRequest | null>(null);
  const [sortField, setSortField] = useState<keyof ProcurementRequest>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>('desc');
  const [loading, setLoading] = useState(false);
  const [isMailsModalOpen, setIsMailsModalOpen] = useState(false);
  const [selectedRequestForMails, setSelectedRequestForMails] = useState<ProcurementRequest | null>(null);

  // Column visibility state - load from localStorage or use defaults
  const [visibleColumns, setVisibleColumns] = useState<{
    [key: string]: boolean;
  }>(() => {
    const savedColumns = localStorage.getItem('procurementTableColumns');
    if (savedColumns) {
      try {
        const parsed = JSON.parse(savedColumns);
        // Ensure all required columns exist, add missing ones with defaults
        const defaultColumns = {
    id: true,
    item: true,
    category: true,
    status: true,
    numberOfQuotes: true,
          rfqCount: true,
          budget: true,
          currency: true,
    owner: true,
    date: true,
    actions: true
        };
        return { ...defaultColumns, ...parsed };
      } catch (error) {
        console.error('Error parsing saved column visibility:', error);
      }
    }
    // Default columns if no saved data
    return {
      id: true,
      item: true,
      category: true,
      status: true,
      numberOfQuotes: true,
      rfqCount: true,
      budget: true,
      currency: true,
      owner: true,
      date: true,
      actions: true
    };
  });

  // Column visibility dropdown state
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  
  // Column order state - load from localStorage or use defaults
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    // Clear old column order to force reset
    localStorage.removeItem('procurementTableColumnOrder');
    
    // Default order with currency right after budget
    const defaultOrder = ['id', 'item', 'category', 'status', 'numberOfQuotes', 'rfqCount', 'budget', 'currency', 'owner', 'date'];
    return defaultOrder;
  });
  
  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Column filtering state
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string;
  }>({
    id: '',
    item: '',
    category: '',
    status: '',
    numberOfQuotes: '',
    rfqCount: '',
    budget: '',
    currency: '',
    owner: '',
    date: ''
  });

  // Active filter dropdown
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Get user role from localStorage
  React.useEffect(() => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        setUserRole(user.role || 'user');
      } catch (error) {
        console.error('Error parsing user info:', error);
      }
    }
  }, []);

  // Loading state management - only show loading for actual data changes
  useEffect(() => {
    if (data.length > 0) {
      setLoading(true);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 200); // Reduced loading time for better UX

      return () => clearTimeout(timer);
    }
  }, [data.length]); // Only trigger on data length changes


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSortIndicator = (direction: 'asc' | 'desc') => {
    return direction === 'asc' ? '↑' : '↓';
  };

  const handleSort = (field: keyof ProcurementRequest) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> none
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection('none');
    } else {
        setSortDirection('asc');
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Column filter handlers with immediate UI update
  const handleColumnFilter = (field: string, value: string) => {
    // Immediately update the filter state for responsive UI
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterToggle = (field: string) => {
    setActiveFilter(activeFilter === field ? null : field);
  };

  const clearAllFilters = () => {
    setColumnFilters({
      id: '',
      item: '',
      category: '',
      status: '',
      numberOfQuotes: '',
      rfqCount: '',
      budget: '',
      nextAction: '',
      owner: '',
      date: ''
    });
    setActiveFilter(null);
  };

  const clearColumnFilter = (field: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: ''
    }));
  };

  // Column visibility handlers
  const toggleColumnVisibility = (columnKey: string) => {
    setVisibleColumns(prev => {
      const newColumns = {
      ...prev,
      [columnKey]: !prev[columnKey]
      };
      // Save to localStorage
      localStorage.setItem('procurementTableColumns', JSON.stringify(newColumns));
      return newColumns;
    });
  };

  const showAllColumns = () => {
    const allColumns = {
      id: true,
      item: true,
      category: true,
      status: true,
      numberOfQuotes: true,
      rfqCount: true,
      budget: true,
      owner: true,
      date: true,
      actions: true
    };
    setVisibleColumns(allColumns);
    // Save to localStorage
    localStorage.setItem('procurementTableColumns', JSON.stringify(allColumns));
  };

  const resetToDefaults = () => {
    const defaultColumns = {
      id: true,
      item: true,
      category: true,
      status: true,
      numberOfQuotes: true,
      rfqCount: true,
      budget: true,
      owner: true,
      date: true,
      actions: true
    };
    const defaultOrder = ['id', 'item', 'category', 'status', 'numberOfQuotes', 'rfqCount', 'budget', 'owner', 'date'];
    
    setVisibleColumns(defaultColumns);
    setColumnOrder(defaultOrder);
    
    // Save to localStorage
    localStorage.setItem('procurementTableColumns', JSON.stringify(defaultColumns));
    localStorage.setItem('procurementTableColumnOrder', JSON.stringify(defaultOrder));
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumn: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetColumn) {
      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(targetColumn);
      
      // Remove dragged column from its current position
      newOrder.splice(draggedIndex, 1);
      // Insert at target position
      newOrder.splice(targetIndex, 0, draggedColumn);
      
      setColumnOrder(newOrder);
      localStorage.setItem('procurementTableColumnOrder', JSON.stringify(newOrder));
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const toggleColumnVisibilityDropdown = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  // PDF Download function
  const handleDownloadPDF = async (e: React.MouseEvent, request: ProcurementRequest) => {
    e.stopPropagation(); // Prevent row click
    setSelectedRequestForPDF(request);
    setIsLanguageModalOpen(true);
  };

  const handleMailsClick = async (e: React.MouseEvent, request: ProcurementRequest) => {
    e.stopPropagation(); // Prevent row click
    console.log('🔍 DEBUG: Mails button clicked for request:', request._id || request.id);
    setSelectedRequestForMails(request);
    setIsMailsModalOpen(true);
  };

  // Actual PDF download with language
  const downloadPDFWithLanguage = async (language: string) => {
    if (!selectedRequestForPDF) return;
    
    try {
      const response = await fetch(API_ENDPOINTS.GENERATE_RFQ_PDF(selectedRequestForPDF.id.toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': language,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `RFQ_${selectedRequestForPDF.id}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download PDF');
        alert(t('common.pdf_download_failed'));
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(t('common.pdf_download_failed'));
    } finally {
      setIsLanguageModalOpen(false);
      setSelectedRequestForPDF(null);
    }
  };

  // Render column header based on column key
  const renderColumnHeader = (columnKey: string) => {
    const isDragging = draggedColumn === columnKey;
    const isDragOver = dragOverColumn === columnKey;
    
    const headerContent = {
      id: (
                    <div className="column-header">
                      <span onClick={() => handleSort('id')} className="sortable-header">
            {t('common.request_id')}
            {sortField === 'id' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.id ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('id')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('id')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'id' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_request_id')}
                            value={columnFilters.id}
                            onChange={(e) => handleColumnFilter('id', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('id')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      item: (
                    <div className="column-header">
                      <span onClick={() => handleSort('item')} className="sortable-header">
            {t('common.item')}
            {sortField === 'item' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.item ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('item')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('item')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'item' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_item')}
                            value={columnFilters.item}
                            onChange={(e) => handleColumnFilter('item', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('item')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      category: (
                    <div className="column-header">
                      <span onClick={() => handleSort('category')} className="sortable-header">
            {t('common.category')}
            {sortField === 'category' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.category ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('category')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('category')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'category' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_category')}
                            value={columnFilters.category}
                            onChange={(e) => handleColumnFilter('category', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('category')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      status: (
                    <div className="column-header">
                      <span onClick={() => handleSort('status')} className="sortable-header">
            {t('common.status')}
            {sortField === 'status' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.status ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('status')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('status')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'status' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_status')}
                            value={columnFilters.status}
                            onChange={(e) => handleColumnFilter('status', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('status')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      numberOfQuotes: (
                    <div className="column-header">
                      <span onClick={() => handleSort('numberOfQuotes')} className="sortable-header">
            {t('common.number_of_quotes')}
            {sortField === 'numberOfQuotes' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.numberOfQuotes ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('numberOfQuotes')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('numberOfQuotes')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'numberOfQuotes' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_quotes')}
                            value={columnFilters.numberOfQuotes}
                            onChange={(e) => handleColumnFilter('numberOfQuotes', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('numberOfQuotes')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      rfqCount: (
                    <div className="column-header">
          <span onClick={() => handleSort('rfqCount')} className="sortable-header">
            {t('common.rfq_count')}
            {sortField === 'rfqCount' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
            className={`filter-button ${columnFilters.rfqCount ? 'active' : ''}`}
            onClick={() => handleFilterToggle('rfqCount')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
            onClick={() => toggleColumnVisibility('rfqCount')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
          {activeFilter === 'rfqCount' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_rfq_count')}
                value={columnFilters.rfqCount}
                onChange={(e) => handleColumnFilter('rfqCount', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('rfqCount')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      budget: (
                    <div className="column-header">
          <span onClick={() => handleSort('budget')} className="sortable-header">
            {t('common.budget')}
            {sortField === 'budget' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
            className={`filter-button ${columnFilters.budget ? 'active' : ''}`}
            onClick={() => handleFilterToggle('budget')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
            onClick={() => toggleColumnVisibility('budget')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
          {activeFilter === 'budget' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_budget')}
                value={columnFilters.budget}
                onChange={(e) => handleColumnFilter('budget', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('budget')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      currency: (
        <div className="column-header">
          <span onClick={() => handleSort('currency')} className="sortable-header">
            {t('common.currency')}
            {sortField === 'currency' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
              </span>
            )}
          </span>
          <button 
            className={`filter-button ${columnFilters.currency ? 'active' : ''}`}
            onClick={() => handleFilterToggle('currency')}
          >
            🔍
          </button>
          <button 
            className="column-hide-button"
            onClick={() => toggleColumnVisibility('currency')}
            title={t('common.hide_column')}
          >
            ×
          </button>
          {activeFilter === 'currency' && (
            <div className="filter-dropdown">
              <input
                type="text"
                placeholder={t('common.filter_currency')}
                value={columnFilters.currency}
                onChange={(e) => handleColumnFilter('currency', e.target.value)}
                autoFocus
              />
              <div className="filter-actions">
                <button onClick={() => clearColumnFilter('currency')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
              </div>
            </div>
          )}
        </div>
      ),
      owner: (
                    <div className="column-header">
                      <span onClick={() => handleSort('owner')} className="sortable-header">
            {t('common.owner')}
            {sortField === 'owner' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.owner ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('owner')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('owner')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'owner' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_owner')}
                            value={columnFilters.owner}
                            onChange={(e) => handleColumnFilter('owner', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('owner')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      ),
      date: (
                    <div className="column-header">
                      <span onClick={() => handleSort('date')} className="sortable-header">
            {t('common.date')}
            {sortField === 'date' && sortDirection !== 'none' && (
              <span className={`sort-indicator ${sortDirection}`}>
                {getSortIndicator(sortDirection)}
                          </span>
                        )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.date ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('date')}
                      >
                        🔍
                      </button>
                      <button 
                        className="column-hide-button"
                        onClick={() => toggleColumnVisibility('date')}
                        title={t('common.hide_column')}
                      >
                        ×
                      </button>
                      {activeFilter === 'date' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                placeholder={t('common.filter_date')}
                            value={columnFilters.date}
                            onChange={(e) => handleColumnFilter('date', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                <button onClick={() => clearColumnFilter('date')}>{t('common.clear')}</button>
                <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
      )
    };

    return (
      <th 
        key={columnKey}
        draggable
        onDragStart={(e) => handleDragStart(e, columnKey)}
        onDragOver={(e) => handleDragOver(e, columnKey)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, columnKey)}
        onDragEnd={handleDragEnd}
        className={`draggable-column ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
      >
        <div className="drag-handle">⋮⋮</div>
        {headerContent[columnKey as keyof typeof headerContent]}
                  </th>
    );
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.column-visibility-btn') && !target.closest('.column-visibility-dropdown')) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  // Filter and sort data with optimized performance
  const filteredAndSortedData = React.useMemo(() => {
    // Only filter if there are active filters
    const hasActiveFilters = Object.values(columnFilters).some(filter => filter.trim() !== '');
    
    if (!hasActiveFilters) {
      // If no filters, just sort the original data
      if (sortDirection === 'none') {
        return data;
      }
      
      return [...data].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return 0;
      });
    }

    // Apply filters only when needed
    let filteredData = data.filter(request => {
      const requestId = typeof request.id === 'number' ? request.id.toString() : request.id;
      
      // Only check filters that have values
      if (columnFilters.id && !requestId.toLowerCase().includes(columnFilters.id.toLowerCase())) return false;
      if (columnFilters.item && !request.item.toLowerCase().includes(columnFilters.item.toLowerCase())) return false;
      if (columnFilters.category && !request.category.toLowerCase().includes(columnFilters.category.toLowerCase())) return false;
      if (columnFilters.status && !request.status.toLowerCase().includes(columnFilters.status.toLowerCase())) return false;
      if (columnFilters.numberOfQuotes && !request.numberOfQuotes.toString().includes(columnFilters.numberOfQuotes)) return false;
      if (columnFilters.rfqCount && !request.rfqCount.toString().includes(columnFilters.rfqCount)) return false;
      if (columnFilters.budget && !(request.budget || 0).toString().includes(columnFilters.budget)) return false;
      if (columnFilters.currency && request.currency && !request.currency.toLowerCase().includes(columnFilters.currency.toLowerCase())) return false;
      if (columnFilters.owner && !request.owner.toLowerCase().includes(columnFilters.owner.toLowerCase())) return false;
      if (columnFilters.date && !request.date.toLowerCase().includes(columnFilters.date.toLowerCase())) return false;
      
      return true;
    });

    // Sort data
    if (sortDirection !== 'none') {
      filteredData.sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return 0;
      });
    }

    return filteredData;
  }, [data, columnFilters, sortField, sortDirection]);

  const handleRowClick = (request: ProcurementRequest) => {
    // Extract the actual request ID from the request object
    // Backend'den gelen _id'yi öncelikle kullan, yoksa id'yi kullan
    const requestId = request._id || request.id.toString();
    
    // Check if this is a completed request
    if (requestId && request.status === 'completed') {
      setSelectedRequestId(requestId);
      setIsCompletedModalOpen(true);
      return;
    }
    
    // Don't open detail modal for revision_required status, select_supplier phase, or approved phase
    // Let the parent component handle it
    console.log('🔍 DEBUG - ProcurementTable handleRowClick - Request status:', request.status);
    if (requestId && request.status !== 'revision_required' && request.status !== 'select_supplier' && request.status !== 'approved') {
      console.log('🔍 DEBUG - ProcurementTable handleRowClick - Opening detail modal for status:', request.status);
      setSelectedRequestId(requestId);
      setIsDetailModalOpen(true);
    } else {
      console.log('🔍 DEBUG - ProcurementTable handleRowClick - NOT opening detail modal for status:', request.status);
    }
    
    // Also call the original onRowClick if provided
    if (onRowClick) {
      onRowClick(request);
    }
  };

  const handleRefreshClick = (e: React.MouseEvent, request: ProcurementRequest) => {
    e.stopPropagation(); // Prevent row click
    if (onRefreshClick) {
      onRefreshClick(request);
    }
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedRequestId(null);
  };

  const isEditDisabled = (status: string) => {
    // Can edit until "send_out_rfq" phase
    // After that, request is locked for editing
    const nonEditableStatuses = [
      'send_out_rfq',
      'receive_responses', 
      'select_supplier',
      'advise_unsuccessful_suppliers',
      'approved',
      'completed',
      'revision_required'
    ];
    return nonEditableStatuses.includes(status);
  };

  const handleEditClick = (e: React.MouseEvent, request: ProcurementRequest) => {
    e.stopPropagation(); // Prevent row click
    if (onEditClick) {
      onEditClick(request);
    } else {
      console.log('Edit request:', request);
    }
  };

  return (
    <>
      <div className="procurement-table-container">
        <div className="procurement-table-header">
          <h2>Procurement Requests</h2>
          <div className="header-actions">
            <button 
              className="column-visibility-btn"
              onClick={toggleColumnVisibilityDropdown}
              title={t('common.column_visibility')}
            >
              ⋯
            </button>
            {showColumnVisibility && (
              <div className="column-visibility-dropdown">
                <div className="column-visibility-header">
                  <span>{t('common.column_visibility')}</span>
                  <div className="column-visibility-buttons">
                    <button onClick={showAllColumns} className="show-all-btn">
                      {t('common.show_all')}
                    </button>
                    <button onClick={resetToDefaults} className="reset-defaults-btn">
                      {t('common.reset_to_defaults')}
                    </button>
                  </div>
                </div>
                <div className="column-visibility-list">
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.id}
                      onChange={() => toggleColumnVisibility('id')}
                    />
                    <span>{t('common.request_id')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.item}
                      onChange={() => toggleColumnVisibility('item')}
                    />
                    <span>{t('common.item')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.category}
                      onChange={() => toggleColumnVisibility('category')}
                    />
                    <span>{t('common.category')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.status}
                      onChange={() => toggleColumnVisibility('status')}
                    />
                    <span>{t('common.status')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.numberOfQuotes}
                      onChange={() => toggleColumnVisibility('numberOfQuotes')}
                    />
                    <span>{t('common.number_of_quotes')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.rfqCount}
                      onChange={() => toggleColumnVisibility('rfqCount')}
                    />
                    <span>{t('common.rfq_count')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.budget}
                      onChange={() => toggleColumnVisibility('budget')}
                    />
                    <span>{t('common.budget')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.currency}
                      onChange={() => toggleColumnVisibility('currency')}
                    />
                    <span>{t('common.currency')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.owner}
                      onChange={() => toggleColumnVisibility('owner')}
                    />
                    <span>{t('common.owner')}</span>
                  </label>
                  <label className="column-visibility-item">
                    <input
                      type="checkbox"
                      checked={visibleColumns.date}
                      onChange={() => toggleColumnVisibility('date')}
                    />
                    <span>{t('common.date')}</span>
                  </label>
                </div>
              </div>
            )}
            <button 
              className="clear-all-filters-btn"
              onClick={clearAllFilters}
              disabled={
                Object.values(columnFilters).every(filter => !filter) ||
                loading
              }
            >
              {loading ? t('common.loading') : t('common.clear_all_filters')}
            </button>
          </div>
        </div>
        <div className="procurement-table-wrapper">
          <table className="procurement-table">
            <thead>
              <tr>
                {columnOrder.map(columnKey => {
                  if (visibleColumns[columnKey]) {
                    return renderColumnHeader(columnKey);
                  }
                  return null;
                })}
                                  {visibleColumns.actions && (
                  <th>{t('common.actions')}</th>
                  )}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length + (visibleColumns.actions ? 1 : 0)} className="no-data-cell">
                    <div className="no-data-content">
                      <p>{t('common.no_requests_found')}</p>
                      <p className="no-data-hint">{t('common.try_adjusting_filters')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map((request, index) => (
                <tr 
                  key={request.id} 
                  className={`${index % 2 === 0 ? 'even-row' : 'odd-row'} clickable-row`}
                  onClick={() => handleRowClick(request)}
                >
                  {columnOrder.map(columnKey => {
                    if (!visibleColumns[columnKey]) return null;
                    
                    switch (columnKey) {
                      case 'id':
                        return <td key={columnKey} className="request-id" title={request.id.toString()}>{request.id}</td>;
                      case 'item':
                        return <td key={columnKey} className="item-name" title={request.item}>{request.item}</td>;
                      case 'category':
                        return <td key={columnKey} className="category" title={request.category}>{request.category}</td>;
                      case 'status':
                        return (
                          <td key={columnKey} className="status-cell">
                      <StatusBadge status={request.status} />
                    </td>
                        );
                      case 'numberOfQuotes':
                        return <td key={columnKey} className="quotes-count" title={request.numberOfQuotes.toString()}>{request.numberOfQuotes}</td>;
                      case 'rfqCount':
                        return <td key={columnKey} className="rfq-count" title={request.rfqCount.toString()}>{request.rfqCount}</td>;
                      case 'budget':
                        return (
                          <td key={columnKey} className="budget" title={(request.budget || 0).toString()}>
                            {(request.budget || 0).toLocaleString()}
                          </td>
                        );
                      case 'currency':
                        return (
                          <td key={columnKey} className="currency" title={request.currency || 'N/A'}>
                            {request.currency || 'N/A'}
                          </td>
                        );
                      case 'owner':
                        return <td key={columnKey} className="owner" title={request.owner}>{request.owner}</td>;
                      case 'date':
                        return <td key={columnKey} className="date" title={formatDate(request.date)}>{formatDate(request.date)}</td>;
                      default:
                        return null;
                    }
                  })}
                  {visibleColumns.actions && (
                    <td className="actions-cell">
                      {/* Edit Button - Only clickable until RFQ phase */}
                      <button 
                        className={`edit-button ${isEditDisabled(request.status) ? 'disabled' : ''}`}
                        onClick={(e) => !isEditDisabled(request.status) && handleEditClick(e, request)}
                        title={isEditDisabled(request.status) ? `Cannot edit in ${request.status} phase` : "Edit Request"}
                        disabled={isEditDisabled(request.status)}
                      >
                        Edit
                      </button>
                      
                      {/* Refresh/Duplicate Button */}
                      <button 
                        className="refresh-button"
                        onClick={(e) => handleRefreshClick(e, request)}
                        title="Duplicate Request"
                      >
                        Reorder
                      </button>

                      {/* Download PDF Button */}
                      <button 
                        className="download-pdf-button"
                        onClick={(e) => handleDownloadPDF(e, request)}
                        title={t('common.download_rfq_pdf')}
                      >
                        Download RFQ
                      </button>

                      {/* Mails Button */}
                      <button 
                        className="mails-button"
                        onClick={(e) => handleMailsClick(e, request)}
                        title="View Email Conversations"
                      >
                        Mails
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
            </tbody>
          </table>
        </div>
        
        {!loading && filteredAndSortedData.length === 0 && (
          <div className="no-data">
            <p>{t('common.no_requests_found')}</p>
          </div>
        )}
      </div>

      {/* Request Detail Modal */}
      {selectedRequestId && (
        <RequestDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetailModal}
          requestId={selectedRequestId}
          userRole={userRole}
        />
      )}

      {/* Completed Request Modal */}
      {selectedRequestId && (
        <CompletedRequestModal
          isOpen={isCompletedModalOpen}
          onClose={() => setIsCompletedModalOpen(false)}
          requestId={selectedRequestId}
        />
      )}

      {/* Language Selection Modal */}
      {isLanguageModalOpen && (
        <div className="modal-overlay" onClick={() => setIsLanguageModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('common.select_language')}</h3>
              <button 
                className="modal-close-button"
                onClick={() => setIsLanguageModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('common.select_language_description')}</p>
              <div className="language-options">
                <button 
                  className="language-button"
                  onClick={() => downloadPDFWithLanguage('en')}
                >
                  🇺🇸 English
                </button>
                <button 
                  className="language-button"
                  onClick={() => downloadPDFWithLanguage('tr')}
                >
                  🇹🇷 Türkçe
                </button>
                <button 
                  className="language-button"
                  onClick={() => downloadPDFWithLanguage('de')}
                >
                  🇩🇪 Deutsch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Emails Modal */}
      <RequestEmailsModal
        isOpen={isMailsModalOpen}
        onClose={() => {
          setIsMailsModalOpen(false);
          setSelectedRequestForMails(null);
        }}
        request={selectedRequestForMails}
      />
    </>
  );
};

export default ProcurementTable; 